import React, { useState, useEffect, useRef, useCallback } from "react";
import {
Box,
Card,
Typography,
TextField,
Button,
MenuItem,
Checkbox,
FormControlLabel,
Snackbar,
Alert,
CircularProgress,
Stack,
InputAdornment,
IconButton, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import { useSelector } from "react-redux";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";

import { useLocation, useNavigate } from "react-router-dom";
import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import { adminPageSx } from "../utils/adminResponsive";

const safeMessage = (data, fallback) => typeof data?.message === "string" && data.message.trim() ? data.message : fallback;
async function profileRequest(path, options = {}) {
  const controller = new AbortController();
  let timer;
  try {
    return await Promise.race([
      adminFetch(path, {...options, signal:controller.signal}),
      new Promise(resolve => { timer = setTimeout(() => {controller.abort(); resolve(null);},15000); })
    ]);
  } finally { clearTimeout(timer); }
}
function validSnapshot(row, id) {
  return row && String(row.id) === String(id) && /^[0-9]{1,20}$/.test(String(row.revision ?? '')) &&
    ['first_name','last_name','status'].every(key => typeof row[key] === 'string') &&
    (row.email === null || typeof row.email === 'string') &&
    (row.pending_request == null || (typeof row.pending_request === 'object' && /^[1-9][0-9]*$/.test(String(row.pending_request.id)) &&
      ['pending','needs_review'].includes(row.pending_request.status) && typeof row.pending_request.operation_type === 'string' &&
      (row.pending_request.operation_type !== 'EMAIL_CHANGE' || typeof row.pending_request.email === 'string')));
}
export default function ModifyUser(){

const { state } = useLocation();
const navigate = useNavigate();

const user = state?.user;
const superAdmin = useSelector(s => s.auth.admin) === "super_admin";
const [snapshot, setSnapshot] = useState(null);
const [profileError,setProfileError] = useState("");
const [profileLoading,setProfileLoading] = useState(false);
const [reconciliation,setReconciliation] = useState("");
const [saveIssue,setSaveIssue] = useState("");
const uncertain = useRef(null);
const reading = useRef(false);
const [reason, setReason] = useState("");
const [confirmation, setConfirmation] = useState(null);
const [adminPassword, setAdminPassword] = useState("");
const saving = useRef(false);
const customerId = user?.id;
const loadCustomer = useCallback(async () => {
  if (reading.current) return;
  reading.current=true; setProfileLoading(true); setProfileError(""); setSnapshot(null);
  try {
    if (!customerId) throw Error('Select a customer from Users first.');
    const data = await profileRequest(`/api/user/modify-user/${customerId}`);
    if (data?.status !== true || !validSnapshot(data.user,customerId)) throw Error(safeMessage(data,'Unable to load customer details. Please retry.'));
    const row=data.user, attempted=uncertain.current;
    setSnapshot(row);setFormData(old=>({...old,...row,email:row.email || ''}));
    if (attempted) {
      const pending=Object.hasOwn(attempted,'email') && row.pending_request?.operation_type==='EMAIL_CHANGE' && row.pending_request.email===attempted.email;
      const matches=Object.entries(attempted).every(([key,value])=>row[key]===value);
      setReconciliation(pending ? 'The email change is recorded as pending approval. The current email remains unchanged.' : matches ? 'The saved customer record now contains the requested values. No repeat submission is needed.' : 'The current customer record has been reloaded. Review it and confirm any further changes before submitting again.');
      uncertain.current=null;setReason('');
    }
  } catch (_) { setProfileError('Unable to load customer details. Please retry before making changes.'); }
  finally {setProfileLoading(false);reading.current=false;}
},[customerId]);
useEffect(()=>{if(superAdmin)loadCustomer();},[superAdmin,loadCustomer]);
const reviewChanges = () => {
  if (!snapshot || profileLoading || loading || snapshot.pending_request) return;
  const changes = Object.fromEntries(["first_name","last_name","email"].map(key => [key, key === "email" ? formData[key].trim().toLowerCase() : formData[key].normalize("NFC").trim().replace(/\s+/g," ")]).filter(([key,value]) => value !== (snapshot[key] || "")));
  if (!Object.keys(changes).length) return showSnackbar("No changes to save", "error");
  if (changes.email !== undefined && Object.keys(changes).length > 1) return showSnackbar("Save name corrections and email changes separately", "error");
  if (Object.entries(changes).some(([key,value]) => key === "email" ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) : !value)) return showSnackbar("Enter valid customer details", "error");
  if (reason.trim().length < 10 || reason.trim().split(/\s+/).length < 3) return showSnackbar("Provide a meaningful reason of at least 10 characters and three words", "error");
  setConfirmation(changes);
};
const saveSensitive = async () => {
  if (saving.current || !snapshot || !confirmation || reading.current) return;
  saving.current=true;setLoading(true);setReconciliation('');setSaveIssue('');
  const attempted={...confirmation};
  try {
    const data=await profileRequest('/api/user/modify-user',{method:'PUT',body:JSON.stringify({user_id:user.id,changes:attempted,reason:reason.trim(),confirmed:true,admin_password:adminPassword,expected_revision:snapshot.revision,expected_values:{first_name:snapshot.first_name,last_name:snapshot.last_name,email:snapshot.email}})});
    setConfirmation(null);setAdminPassword('');
    if (data?.status !== true || typeof data.pending_email !== 'boolean' || !/^[1-9][0-9]*$/.test(String(data.request_id))) {
      uncertain.current=attempted;
      setSaveIssue(safeMessage(data,'The save outcome could not be confirmed.'));
      setReconciliation(safeMessage(data,'The save outcome could not be confirmed. Checking the saved record…'));
    } else {setReason('');showSnackbar(data.warning || data.message || 'Changes recorded.',data.warning?'warning':'success');}
    await loadCustomer();
  } catch (_) {
    uncertain.current=attempted;setConfirmation(null);setAdminPassword('');
    setReconciliation('The save outcome could not be confirmed. Checking the saved record…');
    await loadCustomer();
  } finally {setAdminPassword('');setLoading(false);saving.current=false;}
};

const [formData,setFormData] = useState({
first_name: user?.first_name || "",
last_name: user?.last_name || "",
email: user?.email || "",
phone: user?.phone || "",
status: user?.status || "active",
password: ""
});

const [notify,setNotify] = useState(true);
const [loading,setLoading] = useState(false);
const [showPassword,setShowPassword] = useState(false);


const [snackbar,setSnackbar] = useState({
open:false,
message:"",
severity:"success"
});

const showSnackbar = (message,severity="success")=>{
setSnackbar({open:true,message,severity});
};

const handleCloseSnackbar = ()=>{
setSnackbar({...snackbar,open:false});
};


const update = (field,value)=>{
setFormData(prev=>({...prev,[field]:value}));
};




const handleUpdate = async () => {

setLoading(true);

try{

const payload = {
user_id: user.id,
status: formData.status,
password:formData.password,
notify
};





const data = await adminFetch("/api/user/modify-user", {
  method: "PUT",
  body:JSON.stringify(payload)
});




if(!data.status){
showSnackbar(data.message || "Update failed","error");
setLoading(false);
return;
}

showSnackbar(data.message || "User updated successfully","success");

setTimeout(()=>{
navigate("/admin/users");
},2000);

}catch(err){
console.log(err);
showSnackbar("Server error. Try again.","error");
}

setLoading(false);
};


return(

<Box sx={{ ...adminPageSx }}>

<Card sx={{ p:{ xs:2, sm:4 }, maxWidth:700, mx:"auto" }}>

<Typography
variant="h5"
sx={{mb:3, color:Colors.accent, fontWeight:700}}
>
Modify User
</Typography>



{superAdmin && profileLoading && <Alert severity="info">Loading customer details…</Alert>}
{superAdmin && profileError && <Alert severity="error" sx={{mb:2}} action={<Button color="inherit" disabled={profileLoading || loading} onClick={()=>loadCustomer()}>Retry</Button>}>{profileError}</Alert>}
{superAdmin && saveIssue && <Alert severity="warning" sx={{mb:2}}>{saveIssue}</Alert>}
{superAdmin && reconciliation && <Alert severity="info" sx={{mb:2}}>{reconciliation}</Alert>}
{superAdmin && snapshot?.pending_request && <Alert severity="info" sx={{mb:2}}>A Profile Center request is pending. Resolve it before making another profile change.</Alert>}
<Stack spacing={2}>

<TextField
label="First Name"
value={formData.first_name}
fullWidth
onChange={e=>update("first_name",e.target.value)}
InputProps={{readOnly:!superAdmin || !snapshot || loading}}
/>

<TextField
label="Last Name"
value={formData.last_name}
fullWidth
onChange={e=>update("last_name",e.target.value)}
InputProps={{readOnly:!superAdmin || !snapshot || loading}}
/>

<TextField
label="Email"
value={formData.email}
fullWidth
onChange={e=>update("email",e.target.value)}
InputProps={{readOnly:!superAdmin || !snapshot || loading}}
/>

<TextField
label="Phone"
value={formData.phone}
fullWidth
InputProps={{readOnly:true}}
/>

{superAdmin && <>
<TextField label="Reason for Modification" required multiline minRows={2} value={reason} onChange={e=>setReason(e.target.value)} inputProps={{maxLength:500}} disabled={loading}/>
<Alert severity="info">Save names and email separately. Email changes stay pending in Profile Center until customer verification and approval. An unavailable current email requires the existing recovery approval process.</Alert>
<Button variant="contained" onClick={reviewChanges} disabled={!snapshot || loading || profileLoading || Boolean(snapshot.pending_request)}>Review profile changes</Button>
</>}
</Stack>




<TextField
select
label="User Status"
fullWidth
sx={{mt:3}}
value={formData.status}
onChange={(e)=>update("status",e.target.value)}
>

<MenuItem value="active">Active</MenuItem>
<MenuItem value="suspended">Suspended</MenuItem>
<MenuItem value="closed">Closed</MenuItem>

</TextField>




<TextField
label="New Password (optional)"
type={showPassword ? "text" : "password"}
fullWidth
sx={{mt:3}}
value={formData.password}
onChange={(e)=>update("password",e.target.value)}
helperText="Leave empty if you don’t want to change password"
InputProps={{
endAdornment: (
<InputAdornment position="end">
<IconButton onClick={()=>setShowPassword((prev)=>!prev)} edge="end">
{showPassword ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
</IconButton>
</InputAdornment>
)
}}
/>




<FormControlLabel
sx={{mt:2}}
control={
<Checkbox
checked={notify}
onChange={(e)=>setNotify(e.target.checked)}
/>
}
label={`Notify user (${user?.email})`}
/>




<Button
fullWidth
variant="contained"
sx={{
mt:3,
backgroundColor:Colors.accent,
color:"#003300",
fontWeight:700
}}
onClick={handleUpdate}
disabled={loading || profileLoading || !user?.id}
>
{loading ? (
<CircularProgress size={22} sx={{color:"#fff"}}/>
) : (
"Update User"
)}
</Button>

</Card>




<Dialog open={Boolean(confirmation)} onClose={()=>{if(!loading){setConfirmation(null);setAdminPassword("");}}} fullWidth maxWidth="sm">
<DialogTitle>Confirm customer profile changes</DialogTitle>
<DialogContent><Stack spacing={2} sx={{pt:1,overflowWrap:"anywhere"}}>
<Typography>{snapshot?.first_name} {snapshot?.last_name} — Customer ID: {user?.id}</Typography>
{Object.entries(confirmation || {}).map(([key,value])=><Box key={key}><Typography fontWeight={700}>{key.replaceAll("_"," ")}</Typography><Typography>Previous: {snapshot[key] || "Not set"}</Typography><Typography>New: {value}</Typography></Box>)}
<Typography>Reason: {reason}</Typography>
<TextField label="Your Super Admin password" type="password" autoComplete="current-password" value={adminPassword} onChange={e=>setAdminPassword(e.target.value)} disabled={loading}/>
</Stack></DialogContent>
<DialogActions><Button disabled={loading} onClick={()=>{setConfirmation(null);setAdminPassword("");}}>Cancel</Button><Button onClick={saveSensitive} disabled={loading || !adminPassword}>{loading ? "Saving…" : "Confirm changes"}</Button></DialogActions>
</Dialog>
<Snackbar
open={snackbar.open}
autoHideDuration={4000}
onClose={handleCloseSnackbar}
anchorOrigin={{vertical:"top",horizontal:"center"}}
>
<Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
{snackbar.message}
</Alert>
</Snackbar>

</Box>

);

}
